import transformEventSnapshot from 'event-widgets/utils/transformEventSnapshot';
import transformAccountSnapshot from 'event-widgets/utils/transformAccountSnapshot';
import transformEventData from 'event-widgets/utils/transformEventData';
import transformWebsite from 'event-widgets/utils/transformWebsite';
import { LOAD_EVENT_SNAPSHOT, LOAD_ACCOUNT_SNAPSHOT, LOAD_WEBSITE_CONTENT, LOAD_REGISTRATION_PROCESS_CONTENT
} from './actionTypes';
import loadLanguageManagementSettingsAction from 'event-widgets/redux/modules/languageManagementSettings';
import { getRegistrationTypeId, getEventRegistration } from './selectors/currentRegistrant';
import { getRegistrationPathIdOrNull, getRegistrationPathIdOrDefault } from './selectors/currentRegistrationPath';
import { getInitialRegistrationTypeId } from './userSession';
import { parseRegistrationProcessType, typeOfPage } from './website/registrationProcesses';
import { getRegPackId } from './selectors/shared';

/**
 * Loads account snapshot into store and applies any
 * necessary transforms and migrations to work with the client side application.
 * @param accountSnapshot
 * @returns {{type: string, payload: {version: *, account: {contactCustomFields}}}}
 */
export const createLoadAccountSnapshotAction = (accountSnapshot) => {
  return {
    type: LOAD_ACCOUNT_SNAPSHOT,
    payload: {
      version: accountSnapshot.version,
      account: transformAccountSnapshot(accountSnapshot)
    }
  };
};
/**
 * Loads event snapshot into store and applies any
 * necessary transforms and migrations to work with the client side application.
 * @param eventSnapshot
 * @param account
 * @returns {{type: string, payload: {event: {siteEditor}, version: string, appData: {registrationSettings}, website}}}
 */
export const createLoadEventSnapshotAction = (eventSnapshot, account) => {
  const { siteEditor } = eventSnapshot;
  const event = transformEventSnapshot(eventSnapshot);
  return {
    type: LOAD_EVENT_SNAPSHOT,
    payload: {
      event,
      version: eventSnapshot.version,
      appData: transformEventData(siteEditor.eventData, account, event, siteEditor.website),
      website: transformWebsite(siteEditor.website, account)
    }
  };
};

/**
 * Load a page snapshot into current event snapshot
 * Implemented this way to allow dispatch createLoadEventSnapshotAction with minimum knowledge
 */
export const injectPageSnapshot = (pageSnapshot) => {
  return async (dispatch, getState) => {
    const state = getState();
    const pageNotLoaded = pageId => !state.website.pages[pageId];
    if (Object.keys(pageSnapshot.pages).some(pageNotLoaded)) {
      return await dispatch({
        type: LOAD_WEBSITE_CONTENT,
        payload: {
          website: pageSnapshot
        }
      });
    }
  };
};

/**
 * Load registration content (pages, layout items, etc.)
 */
export const loadRegistrationContent = (pageVariety, registrationPathId = null) => {
  return async (dispatch, getState) => {
    const {
      userSession: { eventId },
      eventSnapshotVersion,
      clients: { eventGuestClient }
    } = getState();
    const eventRegistration = getEventRegistration(getState());
    const registrationTypeId = eventRegistration ?
      getRegistrationTypeId(getState()) :
      getInitialRegistrationTypeId(getState());
    const registrationProcess = await eventGuestClient.getRegistrationContent(
      eventId, eventSnapshotVersion, pageVariety.name, registrationPathId, registrationTypeId);
    dispatch({
      type: LOAD_REGISTRATION_PROCESS_CONTENT,
      payload: { pageVariety, registrationPathId, registrationProcess }
    });
  };
};

export const loadLandingWebsitePageContent = (registrationTypeId = null) => {
  return async (dispatch, getState) => {
    const {
      userSession: { eventId },
      eventSnapshotVersion,
      clients: { eventGuestClient },
      website: { pages } = {}
    } = getState();
    const pageNotLoaded = pageId => pages && !pages[pageId];
    const websiteContent = await eventGuestClient.getWebsiteContent(
      eventId, eventSnapshotVersion, registrationTypeId);
    if (websiteContent && websiteContent.pages && Object.keys(websiteContent.pages).some(pageNotLoaded)) {
      return await dispatch({
        type: LOAD_WEBSITE_CONTENT,
        payload: {
          website: websiteContent
        }
      });
    }
  };
};

/**
 * If the current page is part of a reg path other than the one that the registrant is on, get the page id to redirect
 * to for the registrant to get on the right path, generally the first page of their reg path
 */
export function getRedirectPageIdIfOnWrongPath(currentPageId) {
  return async (dispatch, getState) => {
    const processType = typeOfPage(getState(), currentPageId);
    if (!processType || processType.forCurrentRegistrant().pageIds(getState()).includes(currentPageId)) {
      return;
    }
    await dispatch(loadRegistrationContent(processType, getRegistrationPathIdOrDefault(getState())));
    const pages = processType.forCurrentRegistrant().pageIds(getState());
    return pages[0];
  };
}

export const loadLandingPageContent = pageVarietyName => {
  return async (dispatch, getState) => {
    const varietiesToLoad = [];
    const registrationVariety = parseRegistrationProcessType(pageVarietyName);
    if (registrationVariety) {
      varietiesToLoad.push(registrationVariety);
      return await Promise.all(
        varietiesToLoad.map(variety =>
          dispatch(loadRegistrationContent(variety, getRegistrationPathIdOrNull(getState())))));
    }
    const eventRegistration = getEventRegistration(getState());
    const registrationTypeId = eventRegistration ?
      getRegistrationTypeId(getState()) :
      getInitialRegistrationTypeId(getState());
    if (registrationTypeId) {
      return await dispatch(loadLandingWebsitePageContent(registrationTypeId));
    }
  };
};

/**
 * Loads a specific version of event snapshot from the backend and applies any
 * necessary transforms and migrations to work with the client side application.
 */
export const loadEventSnapshotAndTransform = (eventSnapshotVersion) => {
  return async (dispatch, getState) => {
    const {
      userSession: { eventId },
      account,
      eventSnapshotVersion: lastEventSnapshotVersion, // record the state before the API call
      clients: { eventSnapshotClient }
    } = getState();
    const registrationTypeId = getRegistrationTypeId(getState());
    const registrationPathId = getRegistrationPathIdOrNull(getState());
    const registrationPackId = getRegPackId(getState());
    const eventSnapshot = await eventSnapshotClient.getEventSnapshot(eventId,
      { version: eventSnapshotVersion, registrationTypeId, registrationPathId, registrationPackId });
    // check the state after the API call but before applying changes
    let currentEventSnapshotVersion = getState().eventSnapshotVersion;
    if (currentEventSnapshotVersion === lastEventSnapshotVersion) {
      // state did not change during API call, we can apply the change (API response) to the state
      dispatch(createLoadEventSnapshotAction(eventSnapshot, account));
      dispatch(loadLanguageManagementSettings());
    } else {
      // we will fetch the snapshot again
      return dispatch(loadEventSnapshotAndTransform(eventSnapshotVersion,
        { version: eventSnapshotVersion, registrationTypeId, registrationPathId }));
    }
  };
};

/**
 * Reloads the event snapshot from the backend and applies any necessary transforms
 * and migrations to work with the client side application.
 */
export const filterEventSnapshot = (eventSnapshotVersion, registrationTypeId, registrationPathId) => {
  return async (dispatch, getState) => {
    if (!eventSnapshotVersion) {
      throw new Error('Cannot reload event snapshot without an existing version.');
    }
    const { userSession: { eventId }, account, clients: { eventSnapshotClient } } = getState();
    const registrationPackId = getRegPackId(getState());
    const eventSnapshot = await eventSnapshotClient.getEventSnapshot(eventId,
      { version: eventSnapshotVersion, registrationTypeId, registrationPathId, registrationPackId });
    if (eventSnapshot.version !== eventSnapshotVersion) {
      throw new Error(`event snapshot version: ${eventSnapshot.version} returned from getEventSnapshot API,
        does not match with requested version: ${eventSnapshotVersion}`);
    }
    await dispatch(createLoadEventSnapshotAction(eventSnapshot, account));
    dispatch(loadLanguageManagementSettings());
  };
};

/**
 * Loads the account, event and travel(if on) snapshots from the backend and applies any necessary transoforms
 * and migrations to work with the client side application.
 */
export const loadCombinedSnapshot = () => {
  return async (dispatch, getState) => {
    const {
      userSession: { eventId },
      clients: { eventSnapshotClient }
    } = getState();
    const eventRegistration = getEventRegistration(getState());
    const registrationTypeId = eventRegistration ?
      getRegistrationTypeId(getState()) :
      getInitialRegistrationTypeId(getState());
    const registrationPathId = getRegistrationPathIdOrNull(getState());
    const registrationPackId = getRegPackId(getState());
    const snapshots = await Promise.all([
      eventSnapshotClient.getAccountSnapshot(eventId),
      eventSnapshotClient.getEventSnapshot(eventId, { registrationTypeId, registrationPathId, registrationPackId })
    ]);
    const [accountSnapshot, eventSnapshot] = snapshots;
    dispatch(createLoadAccountSnapshotAction(accountSnapshot));
    dispatch(createLoadEventSnapshotAction(eventSnapshot, getState().account));
  };
};

export function loadLanguageManagementSettings() {
  return (dispatch, getState) => {
    const state = getState();
    const { event, text } = state;
    const regPathId = getRegistrationPathIdOrDefault(state);
    dispatch(loadLanguageManagementSettingsAction(event, regPathId, text));
  };
}
